// Supplier changes to orders (PO-008 / PO-009, read-only Phase 1), laid out
// as the mock's Option J.

// An order's review result once the supplier's changes are flagged. The
// Supplier changes tab only lists flagged orders, so only these two appear.
export const OrderLineReviewStatus = {
  LowRisk: 'low_risk',
  HighRisk: 'high_risk',
} as const
export type OrderLineReviewStatus = (typeof OrderLineReviewStatus)[keyof typeof OrderLineReviewStatus]

// A change on a line, as the server tags it.
export const SupplierChangeKind = {
  PriceIncreased: 'price_increased',
  PriceDecreased: 'price_decreased',
  QuantityIncreased: 'quantity_increased',
  QuantityReduced: 'quantity_reduced',
  Cancelled: 'cancelled',
  Removed: 'removed',
  Added: 'added',
} as const
export type SupplierChangeKind = (typeof SupplierChangeKind)[keyof typeof SupplierChangeKind]

// Option J's mark colours: red for what isn't coming, amber for what changed,
// sky for what was added, purple for the kind of product.
export const ChangeTone = {
  Removed: 'removed',
  Changed: 'changed',
  Added: 'added',
  Product: 'product',
} as const
export type ChangeTone = (typeof ChangeTone)[keyof typeof ChangeTone]

export const CHANGE_TONE_CLASSES: Record<ChangeTone, { chip: string; icon: string }> = {
  [ChangeTone.Removed]: { chip: 'border-red-200 bg-red-50 text-red-700', icon: 'text-red-600' },
  [ChangeTone.Changed]: { chip: 'border-amber-200 bg-amber-50 text-amber-800', icon: 'text-amber-700' },
  [ChangeTone.Added]: { chip: 'border-sky-200 bg-sky-50 text-sky-800', icon: 'text-sky-700' },
  [ChangeTone.Product]: { chip: 'border-purple-200 bg-purple-50 text-purple-700', icon: 'text-purple-600' },
}

// Option J's words for each kind ("Line item removed" is ours: J has no such kind).
export const CHANGE_KIND_MARKS: Record<SupplierChangeKind, { label: string; tone: ChangeTone }> = {
  [SupplierChangeKind.PriceIncreased]: { label: 'Price up', tone: ChangeTone.Changed },
  [SupplierChangeKind.PriceDecreased]: { label: 'Price down', tone: ChangeTone.Changed },
  [SupplierChangeKind.QuantityIncreased]: { label: 'Quantity up', tone: ChangeTone.Changed },
  [SupplierChangeKind.QuantityReduced]: { label: 'Quantity down', tone: ChangeTone.Changed },
  [SupplierChangeKind.Cancelled]: { label: 'Line item cancelled', tone: ChangeTone.Removed },
  [SupplierChangeKind.Removed]: { label: 'Line item removed', tone: ChangeTone.Removed },
  [SupplierChangeKind.Added]: { label: 'Line item added', tone: ChangeTone.Added },
}

export const NON_SUPPLY_SCANNER_LABEL = 'Non-Supply Scanner product'

// The risk of one change, as the server tags it (PO-009 AC2).
export const ChangeRiskLevel = {
  High: 'high',
  Low: 'low',
} as const
export type ChangeRiskLevel = (typeof ChangeRiskLevel)[keyof typeof ChangeRiskLevel]

// Option J's flat badges.
export const BadgeTone = {
  Gray: 'gray',
  Amber: 'amber',
  Red: 'red',
  Purple: 'purple',
} as const
export type BadgeTone = (typeof BadgeTone)[keyof typeof BadgeTone]

export const BADGE_TONE_CLASSES: Record<BadgeTone, string> = {
  [BadgeTone.Gray]: 'border-gray-200 bg-gray-100 text-gray-700',
  [BadgeTone.Amber]: 'border-amber-200 bg-amber-50 text-amber-700',
  [BadgeTone.Red]: 'border-red-200 bg-red-50 text-red-700',
  [BadgeTone.Purple]: 'border-purple-200 bg-purple-50 text-purple-700',
}

export const CHANGE_RISK_BADGE: Record<ChangeRiskLevel, { label: string; tone: BadgeTone }> = {
  [ChangeRiskLevel.High]: { label: 'High risk', tone: BadgeTone.Red },
  [ChangeRiskLevel.Low]: { label: 'Low risk', tone: BadgeTone.Amber },
}

// What a timeline entry says the supplier changed (PO-009 AC5).
export const SupplierTimelineChange = {
  Quantity: 'quantity',
  UnitPrice: 'unit_price',
  Added: 'added',
  Removed: 'removed',
  NoLongerListed: 'no_longer_listed',
} as const
export type SupplierTimelineChange = (typeof SupplierTimelineChange)[keyof typeof SupplierTimelineChange]

export const SUPPLIER_CHANGES_TAB_PARAM = 'supplier-changes'

export const RISK_BADGE: Record<OrderLineReviewStatus, { label: string; className: string }> = {
  [OrderLineReviewStatus.HighRisk]: { label: 'High risk', className: 'border-red-200 bg-red-50 text-red-700' },
  [OrderLineReviewStatus.LowRisk]: { label: 'Low risk', className: 'border-amber-200 bg-amber-50 text-amber-800' },
}

export const SUPPLIER_CHANGE_TIP =
  'What every change the supplier reported moves the ordered total by.'

// Same wait as the other Orders tabs' search box.
export const SUPPLIER_CHANGES_SEARCH_DEBOUNCE_MS = 400
// Orders shown per supplier at first, then how many more each "Show more" adds.
export const SUPPLIER_GROUP_FIRST_PAGE = 5
// The list's page bar starts at this many orders per page, as the other Orders tabs.
export const SUPPLIER_CHANGES_DEFAULT_PAGE_SIZE = 10
export const SUPPLIER_GROUP_MORE_STEP = 10
// A supplier's folded heading is kept for the visit (sessionStorage), as in J.
export const SUPPLIER_GROUP_FOLD_KEY = 'supplier-changes-folded'

// Option J's focus ring and quiet button, in the tenant's brand colour.
export const FOCUS_RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary'
export const QUIET_BUTTON = `inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-xl border-2 border-gray-300 bg-white px-4 text-sm font-semibold text-slate-900 transition-colors hover:border-gray-400 hover:bg-gray-50 ${FOCUS_RING}`
// Whether the review rail beside an order's page is shown (localStorage), as in J.
export const SUPPLIER_CHANGES_RAIL_OPEN_KEY = 'supplier-changes-review-rail-open'
// Wide enough for the review rail beside the page (Tailwind's xl); narrower
// screens get the drawer instead.
export const RAIL_BESIDE_PAGE_QUERY = '(min-width: 1280px)'
